import React, { useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/system';
import { IBooking, bookingTimeCaption2 } from '../Utils/EDCalendarViewUtils';
import EDBox from '../../EDBox/EDBox';
import EDTypography from '../../Typography/EDTypography';
import { useIsSmallScreen } from '../../../Hooks/useFullscreenView';
import * as dateFns from 'date-fns';

interface IProps {
  booking: IBooking;
  onBookingClick?: (ref: EventTarget, booking: IBooking) => void;
  boldTitle?: boolean;
  showLocation?: boolean;
  view: 'List' | 'All';
}

export const EDCalendarViewDayViewBooking: React.FC<IProps> = props => {
  const isSmallScreen = useIsSmallScreen();
  const compact = props.view === 'All';

  const cssVariables = useMemo(
    () =>
      ({
        '--borderColor': props.booking.color ?? 'white',
        '--backgroundColor': props.booking.color
          ? alpha(props.booking.color, 0.7)
          : undefined,
      } as React.CSSProperties),
    [props.booking.color]
  );

  const onBookingClick = props.onBookingClick;

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!event.ctrlKey && !event.shiftKey) {
      event.stopPropagation();
    }
  }, []);

  const handleBookingClick = useCallback(
    (event: React.MouseEvent) => {
      if (!event.ctrlKey && !event.shiftKey && onBookingClick) {
        onBookingClick(event.target, props.booking);
      }
      event.stopPropagation();
    },
    [onBookingClick, props.booking]
  );

  const isPast = useMemo(
    () =>
      props.booking?.endTime != null &&
      dateFns.isBefore(props.booking.endTime, new Date()),
    [props.booking?.endTime]
  );

  const timeCaption2 = bookingTimeCaption2(props.booking);

  return (
    <BookingContainer
      style={cssVariables}
      compact={compact}
      onMouseDown={handleMouseDown}
      onClick={handleBookingClick}
    >
      <BookingTypography
        isSmallScreen={isSmallScreen}
        isPast={isPast}
        compact={compact}
      >
        <TimeText compact={compact}>{timeCaption2}</TimeText>
        {props.boldTitle ? (
          <BoldText compact={compact} isSmallScreen={isSmallScreen}>
            {props.booking.aktCaption}
          </BoldText>
        ) : (
          <CaptionText compact={compact}>
            {props.booking.aktCaption}
          </CaptionText>
        )}
        {props.showLocation && props.booking.aktLocation && (
          <LocationText compact={compact} isSmallScreen={isSmallScreen}>
            {props.booking.aktLocation}
          </LocationText>
        )}
      </BookingTypography>
    </BookingContainer>
  );
};

const BookingContainer = styled(EDBox, {
  name: 'BookingContainer',
  shouldForwardProp: prop => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  display: 'flex',
  alignItems: 'center',
  borderLeft: `${theme.spacing(0.5)} solid var(--borderColor)`,
  marginBottom: compact ? '1px' : theme.spacing(1),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'var(--backgroundColor)',
  },
  height: compact ? '15px' : '16px',
  padding: compact ? '4px' : '8px',
}));

const BookingTypography = styled(EDTypography, {
  name: 'BookingTypography',
  shouldForwardProp: prop =>
    prop !== 'isPast' && prop !== 'compact' && prop !== 'isSmallScreen',
})<{ isPast?: boolean; compact?: boolean; isSmallScreen?: boolean }>(
  ({ isPast, compact, isSmallScreen }) => ({
    fontSize: compact ? '12px' : '14px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...(isPast && { opacity: 0.38 }),
    ...(!compact && !isSmallScreen && { display: 'flex' }),
  })
);

const BoldText = styled('span', {
  name: 'BoldText',
  shouldForwardProp: prop => prop !== 'compact' && prop !== 'isSmallScreen',
})<{ compact?: boolean; isSmallScreen?: boolean }>(
  ({ theme, compact, isSmallScreen }) => ({
    fontWeight: theme.typography.fontWeightBold,
    marginLeft: compact ? '8px' : isSmallScreen ? '12px' : '20px',
    ...(!compact && {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: '0 0 180px',
    }),
  })
);

const CaptionText = styled('span', {
  name: 'CaptionText',
  shouldForwardProp: prop => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  marginLeft: compact ? '3px' : '20px',
  ...(!compact && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: '0 0 180px',
  }),
}));

const LocationText = styled('span', {
  name: 'LocationText',
  shouldForwardProp: prop => prop !== 'isSmallScreen' && prop !== 'compact',
})<{ isSmallScreen?: boolean; compact?: boolean }>(
  ({ isSmallScreen, compact }) => ({
    marginLeft: '12px',
    ...(!compact && {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: isSmallScreen ? '0 0 100px' : '0 0 270px',
    }),
  })
);

const TimeText = styled('span', {
  name: 'TimeText',
  shouldForwardProp: prop => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
  ...(!compact && {
    flex: '0 0 50px',
  }),
}));
